/**
 * Minimap — thumbnail overview of all open panels on the spatial canvas.
 * Fixed bottom-left, glassmorphic style.
 */

import React, { useMemo, useState, useEffect } from 'react';
import type { PanelState } from './panel-layout-store';
import './minimap.css';

// ─── constants ───────────────────────────────────────────────────────────────

/** Logical canvas coordinate space that panels live in */
const CANVAS_W = 3000;
const CANVAS_H = 2000;

/** Rendered minimap dimensions (must match CSS) */
const MAP_W = 160;
const MAP_H = 100;

/** Scale factors */
const SCALE_X = MAP_W / CANVAS_W;
const SCALE_Y = MAP_H / CANVAS_H;

/**
 * Small fixed palette of panel colors keyed by panel type prefix.
 * Unknown types fall back to cycling through the palette by index.
 */
const TYPE_COLORS: Record<string, string> = {
  discord: 'rgba(88, 101, 242, 0.75)',
  x: 'rgba(29, 161, 242, 0.75)',
  feed: 'rgba(29, 161, 242, 0.75)',
  topics: 'rgba(29, 161, 242, 0.75)',
  settings: 'rgba(180, 180, 200, 0.6)',
  preferences: 'rgba(180, 180, 200, 0.6)',
  identity: 'rgba(80, 220, 160, 0.75)',
  'linked-identities': 'rgba(80, 220, 160, 0.75)',
  'public-profile': 'rgba(80, 220, 160, 0.75)',
  'private-chat': 'rgba(240, 120, 60, 0.75)',
  voice: 'rgba(240, 80, 80, 0.75)',
  receipts: 'rgba(200, 170, 60, 0.75)',
  metrics: 'rgba(200, 170, 60, 0.75)',
  'my-accords': 'rgba(160, 100, 240, 0.75)',
  'accord-detail': 'rgba(160, 100, 240, 0.75)',
  groups: 'rgba(100, 200, 240, 0.75)',
  'meeting-notes': 'rgba(100, 220, 130, 0.75)',
  'avid-player': 'rgba(240, 60, 100, 0.75)',
  'avid-channels': 'rgba(240, 60, 100, 0.75)',
  pieces: 'rgba(220, 150, 80, 0.75)',
  troves: 'rgba(220, 150, 80, 0.75)',
  canvas: 'rgba(140, 200, 255, 0.75)',
  objects: 'rgba(140, 200, 255, 0.75)',
  '3d': 'rgba(140, 200, 255, 0.75)',
  'spatial-grid': 'rgba(140, 200, 255, 0.75)',
  'world-map': 'rgba(60, 180, 120, 0.75)',
  business: 'rgba(255, 200, 80, 0.75)',
};

const FALLBACK_PALETTE = [
  'rgba(100, 160, 255, 0.7)',
  'rgba(255, 120, 100, 0.7)',
  'rgba(100, 220, 160, 0.7)',
  'rgba(220, 160, 255, 0.7)',
  'rgba(255, 200, 80, 0.7)',
  'rgba(80, 220, 220, 0.7)',
];

function panelColor(type: string, index: number): string {
  return TYPE_COLORS[type] ?? FALLBACK_PALETTE[index % FALLBACK_PALETTE.length];
}

// ─── types ────────────────────────────────────────────────────────────────────

export interface MinimapProps {
  panels: Map<string, PanelState>;
  viewportWidth: number;
  viewportHeight: number;
  onPanelClick?: (panelId: string) => void;
}

// ─── component ────────────────────────────────────────────────────────────────

export function Minimap({ panels, viewportWidth, viewportHeight, onPanelClick }: MinimapProps) {
  const [scrollPos, setScrollPos] = useState({ x: window.scrollX, y: window.scrollY });

  useEffect(() => {
    const onScroll = () => setScrollPos({ x: window.scrollX, y: window.scrollY });
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /** Sorted panel list so we render a consistent index-based color */
  const panelList = useMemo(
    () => Array.from(panels.values()).filter((p) => !p.minimized),
    [panels]
  );

  /** Find the panel with the highest z-index — that's the "active" one */
  const topZIndex = useMemo(
    () => panelList.reduce((max, p) => (p.zIndex > max ? p.zIndex : max), -Infinity),
    [panelList]
  );

  /** Viewport indicator rect scaled to minimap space */
  const viewportRect = useMemo(() => {
    const x = scrollPos.x * SCALE_X;
    const y = scrollPos.y * SCALE_Y;
    const w = Math.min(viewportWidth * SCALE_X, MAP_W);
    const h = Math.min(viewportHeight * SCALE_Y, MAP_H);
    return { x, y, w, h };
  }, [scrollPos, viewportWidth, viewportHeight]);

  if (panels.size === 0) return null;

  return (
    <div className="minimap-container" role="complementary" aria-label="Canvas minimap">
      <span className="minimap-label">MAP</span>

      {panelList.map((panel, idx) => {
        const color = panelColor(panel.type, idx);
        const isTop = panel.zIndex === topZIndex;

        const left = panel.position.x * SCALE_X;
        const top = panel.position.y * SCALE_Y;
        const width = Math.max(4, panel.size.width * SCALE_X);
        const height = Math.max(3, panel.size.height * SCALE_Y);

        return (
          <div
            key={panel.id}
            className="minimap-panel-rect"
            title={panel.type}
            style={{
              left,
              top,
              width,
              height,
              background: color,
              border: isTop
                ? '1px solid rgba(255,255,255,0.7)'
                : '1px solid rgba(255,255,255,0.15)',
              zIndex: isTop ? 5 : 1,
            }}
            onClick={() => onPanelClick?.(panel.id)}
          />
        );
      })}

      {/* Viewport indicator — only useful if the canvas scrolls */}
      {(viewportRect.w < MAP_W || viewportRect.h < MAP_H) && (
        <div
          className="minimap-viewport-rect"
          style={{
            left: viewportRect.x,
            top: viewportRect.y,
            width: viewportRect.w,
            height: viewportRect.h,
          }}
        />
      )}
    </div>
  );
}

export default Minimap;
